import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CHAT_KEY_REQUEST_SUBTYPE, CHAT_KEY_RESPONSE_SUBTYPE, ChatKeyUnavailableError, chatKeySignMessage, decryptChatPayload, deriveV1KeyBytes, deriveV2KeyBytesFromSignature, encryptChatPayload, generateEcdhPair, importAesKey, unwrapKeyFromPeer, wrapKeyForPeer, } from '../utils/chatCrypto';
const SIGN_RETRY_COOLDOWN_MS = 60_000;
const GUEST_REQUEST_INTERVAL_MS = 15_000;
/**
 * Owns the E2E chat key lifecycle for one chat surface.
 *
 * Wallet devices derive the v2 key lazily (first encrypt/decrypt or first
 * guest key request) from a deterministic EIP-191 signature, cache it for
 * the session, and answer guest key requests with an ECDH-wrapped copy.
 * Guests broadcast key requests until an online wallet device responds.
 * Sending requires the v2 key: the legacy v1 (address-derived) fallback is
 * decrypt-only, kept solely so messages from older clients stay readable
 * during the transition. `keyReady` exposes the v2 state to the UI.
 */
export function useChatCrypto({ room, signMessage, enabled, preloadedV2KeyBytes, canProvideRoomKey, isTrustedResponder, hasOnlineWalletDevice }) {
    const normalizedRoom = useMemo(() => room?.toLowerCase() || null, [room]);
    const v1KeyRef = useRef(null);
    const v2Ref = useRef(null);
    const signInFlightRef = useRef(null);
    const signFailedAtRef = useRef(0);
    const ecdhPairRef = useRef(null);
    const sendSignalRef = useRef(null);
    const requestTimerRef = useRef(null);
    const signMessageRef = useRef(signMessage);
    const preloadedV2KeyBytesRef = useRef(preloadedV2KeyBytes);
    const canProvideRoomKeyRef = useRef(canProvideRoomKey ?? !!signMessage);
    const enabledRef = useRef(enabled);
    const normalizedRoomRef = useRef(normalizedRoom);
    const roomGenerationRef = useRef(0);
    const previousRoomRef = useRef(normalizedRoom);
    signMessageRef.current = signMessage;
    preloadedV2KeyBytesRef.current = preloadedV2KeyBytes;
    canProvideRoomKeyRef.current = canProvideRoomKey ?? !!signMessage;
    enabledRef.current = enabled;
    normalizedRoomRef.current = normalizedRoom;
    const isTrustedResponderRef = useRef(isTrustedResponder);
    isTrustedResponderRef.current = isTrustedResponder;
    const hasOnlineWalletDeviceRef = useRef(hasOnlineWalletDevice);
    hasOnlineWalletDeviceRef.current = hasOnlineWalletDevice;
    /** Nonce binding responses to our outstanding request (guest mode). */
    const requestIdRef = useRef(null);
    const requestRoomRef = useRef(null);
    const requestGenerationRef = useRef(0);
    const [keyReady, setKeyReady] = useState(false);
    const getV1Key = useCallback(async () => {
        if (!normalizedRoom)
            throw new Error('No chat room');
        if (v1KeyRef.current?.room === normalizedRoom)
            return v1KeyRef.current.key;
        const key = await importAesKey(await deriveV1KeyBytes(normalizedRoom));
        v1KeyRef.current = { room: normalizedRoom, key };
        return key;
    }, [normalizedRoom]);
    const setV2Key = useCallback(async (raw, persist) => {
        const key = await importAesKey(raw);
        if (normalizedRoom) {
            v2Ref.current = { room: normalizedRoom, raw, key };
            setKeyReady(true);
            // Never persist raw chat-key material. Device owners retain an
            // encrypted vault; guest keys intentionally live only in memory.
        }
        return key;
    }, [normalizedRoom]);
    /**
     * Returns the v2 key if held or (wallet mode) derivable now. Never throws:
     * returns null when unavailable (signature rejected / guest still waiting).
     */
    const getV2Key = useCallback(async () => {
        if (!normalizedRoom || !enabled)
            return null;
        if (v2Ref.current?.room === normalizedRoom)
            return v2Ref.current.key;
        const preloaded = preloadedV2KeyBytesRef.current;
        if (preloaded) {
            if (preloaded.length !== 32)
                return null;
            return setV2Key(preloaded, false);
        }
        const sign = signMessageRef.current;
        if (!sign)
            return null; // guest: key arrives via ECDH exchange only
        if (Date.now() - signFailedAtRef.current < SIGN_RETRY_COOLDOWN_MS)
            return null;
        if (signInFlightRef.current)
            return signInFlightRef.current;
        const attempt = (async () => {
            try {
                const signature = await sign(chatKeySignMessage(normalizedRoom));
                const raw = await deriveV2KeyBytesFromSignature(signature);
                return await setV2Key(raw, true);
            }
            catch (err) {
                console.warn('[useChatCrypto] v2 key signature failed — sending stays disabled until signed:', err);
                signFailedAtRef.current = Date.now();
                return null;
            }
            finally {
                signInFlightRef.current = null;
            }
        })();
        signInFlightRef.current = attempt;
        return attempt;
    }, [normalizedRoom, enabled, setV2Key]);
    const getV2KeyBytes = useCallback(async () => {
        await getV2Key();
        const held = v2Ref.current;
        return held && held.room === normalizedRoom ? held.raw : null;
    }, [getV2Key, normalizedRoom]);
    const encryptFn = useCallback(async (text) => {
        // v2 only — encryptChatPayload throws ChatKeyUnavailableError when the
        // confirmed key is not held (legacy v1 sending is disabled).
        const v2Key = await getV2Key();
        return encryptChatPayload(text, v2Key);
    }, [getV2Key]);
    const decryptFn = useCallback(async (encrypted) => {
        const [v2Key, v1Key] = await Promise.all([getV2Key(), getV1Key()]);
        try {
            return await decryptChatPayload(encrypted, v2Key, v1Key);
        }
        catch (err) {
            if (err instanceof ChatKeyUnavailableError) {
                // Guest without the key yet — nudge the exchange again.
                requestKeyNow();
            }
            throw err;
        }
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getV2Key, getV1Key]);
    const requestKeyNow = useCallback(() => {
        if (!enabled || !normalizedRoom)
            return;
        if (signMessageRef.current)
            return; // wallet devices derive, never request
        if (v2Ref.current?.room === normalizedRoom)
            return;
        const send = sendSignalRef.current;
        const pair = ecdhPairRef.current;
        if (!send || !pair)
            return;
        if (!requestIdRef.current) {
            requestIdRef.current = `ckr-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
            requestRoomRef.current = normalizedRoom;
            requestGenerationRef.current = roomGenerationRef.current;
        }
        send({
            subtype: CHAT_KEY_REQUEST_SUBTYPE,
            callId: 'chat-key',
            payload: { pub: pair.publicJwk, requestId: requestIdRef.current },
        });
    }, [enabled, normalizedRoom]);
    const retryE2EEKey = useCallback(async () => {
        if (!enabled || !normalizedRoom)
            return 'failed';
        if (v2Ref.current?.room === normalizedRoom)
            return 'ok';
        if (signMessageRef.current) {
            // Wallet device: bypass SIGN_RETRY_COOLDOWN_MS — this is an explicit
            // user action, so re-prompt for the signature right away.
            signFailedAtRef.current = 0;
            const key = await getV2Key();
            return key ? 'ok' : 'failed';
        }
        // Guest: only wallet devices can answer the key request. When presence
        // says none is online, tell the user instead of failing silently.
        if (hasOnlineWalletDeviceRef.current?.() === false)
            return 'no-wallet-device';
        // Nudge the ECDH exchange again; the key arrives asynchronously.
        requestKeyNow();
        return v2Ref.current?.room === normalizedRoom ? 'ok' : 'pending';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, normalizedRoom, getV2Key]);
    const handleKeySignal = useCallback((signal) => {
        if (signal.subtype === CHAT_KEY_REQUEST_SUBTYPE) {
            // Wallet device: answer with the wrapped v2 key.
            (async () => {
                if (!canProvideRoomKeyRef.current)
                    return;
                const responseRoom = normalizedRoomRef.current;
                const responseGeneration = roomGenerationRef.current;
                if (!enabledRef.current || !responseRoom)
                    return;
                const peerPub = signal.payload?.pub;
                if (!peerPub)
                    return;
                const key = await getV2Key();
                const raw = v2Ref.current?.raw;
                if (!canProvideRoomKeyRef.current ||
                    !enabledRef.current ||
                    normalizedRoomRef.current !== responseRoom ||
                    roomGenerationRef.current !== responseGeneration)
                    return;
                if (!key || !raw)
                    return;
                try {
                    const wrapped = await wrapKeyForPeer(raw, peerPub);
                    if (!canProvideRoomKeyRef.current ||
                        !enabledRef.current ||
                        normalizedRoomRef.current !== responseRoom ||
                        roomGenerationRef.current !== responseGeneration ||
                        v2Ref.current?.room !== responseRoom)
                        return;
                    sendSignalRef.current?.({
                        subtype: CHAT_KEY_RESPONSE_SUBTYPE,
                        callId: 'chat-key',
                        to: signal.from,
                        payload: { ...wrapped, requestId: signal.payload?.requestId },
                    });
                }
                catch (err) {
                    console.warn('[useChatCrypto] Failed to wrap chat key for guest:', err);
                }
            })();
            return true;
        }
        if (signal.subtype === CHAT_KEY_RESPONSE_SUBTYPE) {
            (async () => {
                const pair = ecdhPairRef.current;
                if (!pair || v2Ref.current?.room === normalizedRoom)
                    return;
                // Reject responses not bound to our outstanding request nonce.
                if (!requestIdRef.current || signal.payload?.requestId !== requestIdRef.current) {
                    console.warn('[useChatCrypto] Ignoring chat key response with mismatched requestId');
                    return;
                }
                const responseRoom = normalizedRoom;
                const responseGeneration = roomGenerationRef.current;
                if (requestRoomRef.current !== responseRoom ||
                    requestGenerationRef.current !== responseGeneration) {
                    console.warn('[useChatCrypto] Ignoring chat key response from stale room generation');
                    return;
                }
                // STRICT: when a trust checker is configured (guest flows), only a
                // wallet_auth-verified owner device may hand us the room key. An
                // unverifiable responder means no key — never a possibly-poisoned key.
                if (isTrustedResponderRef.current && isTrustedResponderRef.current(signal.from, signal) !== true) {
                    console.warn('[useChatCrypto] Ignoring chat key response from unverified device', signal.from);
                    return;
                }
                try {
                    const raw = await unwrapKeyFromPeer(signal.payload, pair);
                    if (normalizedRoomRef.current !== responseRoom ||
                        roomGenerationRef.current !== responseGeneration ||
                        requestRoomRef.current !== responseRoom ||
                        requestGenerationRef.current !== responseGeneration)
                        return;
                    await setV2Key(raw, true);
                    requestIdRef.current = null;
                    requestRoomRef.current = null;
                    console.log('[useChatCrypto] Received v2 chat key from wallet device');
                }
                catch (err) {
                    console.warn('[useChatCrypto] Failed to unwrap chat key response:', err);
                }
            })();
            return true;
        }
        return false;
    }, [getV2Key, normalizedRoom, setV2Key]);
    const bindSendSignal = useCallback((fn) => {
        sendSignalRef.current = fn;
    }, []);
    const notifyRoomActivity = useCallback(() => {
        requestKeyNow();
    }, [requestKeyNow]);
    // Guest mode: prepare an ECDH pair and poll for the key until received.
    useEffect(() => {
        if (!enabled || !normalizedRoom || signMessage)
            return;
        let cancelled = false;
        (async () => {
            // Hydrate from the session cache first: a guest who already received
            // the key must be able to send immediately (keyReady=true) even if no
            // wallet device is currently online to re-hand it off.
            if (v2Ref.current?.room !== normalizedRoom) {
                await getV2Key();
            }
            else {
                setKeyReady(true);
            }
            if (cancelled || v2Ref.current?.room === normalizedRoom)
                return;
            if (!ecdhPairRef.current) {
                try {
                    ecdhPairRef.current = await generateEcdhPair();
                }
                catch (err) {
                    console.error('[useChatCrypto] Failed to generate ECDH pair:', err);
                    return;
                }
            }
            if (cancelled)
                return;
            requestKeyNow();
            requestTimerRef.current = setInterval(() => {
                if (v2Ref.current?.room === normalizedRoom) {
                    if (requestTimerRef.current) {
                        clearInterval(requestTimerRef.current);
                        requestTimerRef.current = null;
                    }
                    return;
                }
                requestKeyNow();
            }, GUEST_REQUEST_INTERVAL_MS);
        })();
        return () => {
            cancelled = true;
            if (requestTimerRef.current) {
                clearInterval(requestTimerRef.current);
                requestTimerRef.current = null;
            }
        };
    }, [enabled, normalizedRoom, signMessage, requestKeyNow, getV2Key]);
    // Room switch: drop in-memory keys belonging to another room.
    useEffect(() => {
        if (previousRoomRef.current !== normalizedRoom) {
            previousRoomRef.current = normalizedRoom;
            roomGenerationRef.current += 1;
            requestIdRef.current = null;
            requestRoomRef.current = null;
            requestGenerationRef.current = roomGenerationRef.current;
            ecdhPairRef.current = null;
        }
        if (v2Ref.current && v2Ref.current.room !== normalizedRoom) {
            v2Ref.current = null;
            setKeyReady(false);
        }
        if (v1KeyRef.current && v1KeyRef.current.room !== normalizedRoom)
            v1KeyRef.current = null;
    }, [normalizedRoom]);
    // Wallet mode: derive the v2 key proactively so the user is prompted to
    // sign as soon as the chat opens (sending is blocked until keyReady).
    useEffect(() => {
        if (!enabled || !normalizedRoom || (!signMessage && !preloadedV2KeyBytes))
            return;
        if (v2Ref.current?.room === normalizedRoom) {
            setKeyReady(true);
            return;
        }
        void getV2Key();
    }, [enabled, normalizedRoom, signMessage, preloadedV2KeyBytes, getV2Key]);
    return { encryptFn, decryptFn, handleKeySignal, bindSendSignal, notifyRoomActivity, keyReady, getV2KeyBytes, retryE2EEKey };
}
